import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { RowData } from '../types';

interface ShiftLinesProps {
  rows: RowData[];
  activeLevel: number;
  base: number;
  method: 'full' | 'contracted';
}

const ShiftLines: React.FC<ShiftLinesProps> = ({
  rows,
  activeLevel,
  base,
  method,
}) => {
  const NODE_WIDTH = 60;
  const NODE_GAP = 16;
  const NODE_STEP = NODE_WIDTH + NODE_GAP;
  const ROW_SPACING = 119;

  const getRowWidth = (count: number) =>
    count * NODE_WIDTH + (count - 1) * NODE_GAP;
  const getNodeX = (index: number, count: number) => {
    const rowWidth = getRowWidth(count);
    const startX = -rowWidth / 2;
    return index * NODE_STEP + startX + NODE_WIDTH / 2;
  };

  const connections = useMemo(() => {
    const paths: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      key: string;
    }[] = [];

    for (let r = 0; r < activeLevel; r++) {
      const currentRow = rows[r];
      const nextRow = rows[r + 1];
      if (!currentRow || !nextRow) continue;

      const y1 = r * ROW_SPACING;
      const y2 = (r + 1) * ROW_SPACING;

      currentRow.elements.forEach((_, i) => {
        const x1 = getNodeX(i, currentRow.elements.length);

        if (method === 'full') {
          for (let j = 0; j < base; j++) {
            const nextIdx = i * base + j;
            if (nextRow.elements[nextIdx] !== undefined) {
              const x2 = getNodeX(nextIdx, nextRow.elements.length);
              paths.push({ x1, y1, x2, y2, key: `${r}-${i}-${nextIdx}` });
            }
          }
        } else {
          for (let j = 0; j < base; j++) {
            const nextIdx = i + j;
            if (nextRow.elements[nextIdx] !== undefined) {
              const x2 = getNodeX(nextIdx, nextRow.elements.length);
              paths.push({ x1, y1, x2, y2, key: `${r}-${i}-${nextIdx}` });
            }
          }
        }
      });
    }
    return paths;
  }, [rows, activeLevel, base, method]);

  return (
    <svg
      className="pointer-events-none absolute top-0 left-1/2 overflow-visible"
      style={{ zIndex: 0 }}
    >
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(16, 185, 129, 0.15)" />
          <stop offset="100%" stopColor="rgba(59, 130, 246, 0.15)" />
        </linearGradient>
      </defs>
      <g transform={`translate(0, 57)`}>
        {connections.map((path) => (
          <motion.path
            key={path.key}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            d={`M ${path.x1} ${path.y1} L ${path.x2} ${path.y2}`}
            stroke="url(#lineGradient)"
            strokeWidth="1"
            fill="none"
            strokeDasharray="4 4"
          />
        ))}
      </g>
    </svg>
  );
};

export default ShiftLines;
